<script setup lang="ts">
    import { ref } from 'vue';
    import Terms from '@cwd/AiTools/Terms.vue';
    import Modal from '@shared/components/Modal.vue';
    import ModalAiIcon from '@shared/components/ModalAiIcon.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import type { QuestionSummaryReport } from '../../SurveysTypes.d';

    const props = defineProps<{
        report: QuestionSummaryReport | null;
        hasSummaries: boolean;
        loading: boolean;
        generating: boolean;
        generated: boolean;
        termsAccepted: boolean;
        show: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'generate'): void;
        (e: 'accept-terms'): void;
        (e: 'update:show', value: boolean): void;
    }>();

    const showConfirmModal = ref(false);

    function confirmGenerate() {
        showConfirmModal.value = false;
        emit('generate');
    }
</script>

<template>
    <section class="ai-card m-lg-b">
        <h2 class="h3 d-flex align-items-center">
            <ModalAiIcon />
            {{ $Resources.AIAssistantQuestionSummaryHeader }}
        </h2>

        <p class="m-lg-b">
            {{ $Resources.AIAssistantQuestionSummaryInfo }}
            <template v-if="report">
                <template v-if="report.NewAnswersSince">
                    <br />
                    {{ $Resources.AIAssistantQuestionSummaryAnswerSinceLast }}:
                    <strong>{{ report.NewAnswersSince }}</strong>
                </template>
                <template v-if="report.Created">
                    <br />
                    {{ $Resources.AIAssistantLastRunByLabel }} {{ report.CreatedBy?.FirstName }} {{ report.CreatedBy?.LastName }} ({{
                        formatDateTime(report.Created)
                    }}).
                </template>
            </template>
        </p>

        <Terms :termsAccepted="props.termsAccepted" @accept="emit('accept-terms')" />

        <div v-if="!generated" v-show="props.termsAccepted">
            <div class="form-group d-flex gap-lg align-items-center flex-wrap">
                <button type="button" class="btn btn-default" :class="{ 'is-busy': generating }" :disabled="loading" @click="showConfirmModal = true">
                    {{ $Resources.AIAssistantGenerateQuestionSummaries }}
                </button>

                <FormCheckable
                    v-if="hasSummaries"
                    class="m-0-b"
                    :label="$Resources.AIAssistantShowQuestionSummaries"
                    :switch-style="SwitchStyle.Standard"
                    :checked="show"
                    :disabled="loading"
                    @update:model-value="emit('update:show', $event)" />
            </div>
        </div>
    </section>

    <Modal size="lg" icon="ai" :show="showConfirmModal" :title="$Resources.AIAssistantGenerateQuestionSummaries" @close="showConfirmModal = false">
        <template #content>
            <BsAlert
                :type="AlertTypes.Warning"
                :dismissible="false"
                :title="$Resources.AIAssistantDisclaimerStrong"
                :content="$Resources.AIAssistantDisclaimerFollowUp">
                <template #content>
                    <ul>
                        <li>{{ $Resources.AIAssistantQuestionSummariesDisclaimerBody1 }}</li>
                        <li>{{ $Resources.AIAssistantQuestionSummariesDisclaimerBody2 }}</li>
                    </ul>
                </template>
            </BsAlert>

            <p>{{ $Resources.AIAssistantQuestionSummaryInfo }}</p>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :class="{ 'is-busy': generating }" :disabled="loading" @click="confirmGenerate">
                {{ $Resources.AIAssistantGenerateQuestionSummaries }}
            </button>
        </template>
    </Modal>
</template>

